import React, { useState, useEffect, useRef } from "react";
import { useGoogleMaps } from "../../contexts/GoogleMapsContext";

// Define the props interface for the component
interface DropDownMapProps {
  label: string;
  name: string;
  register: any;
  errors: any;
  disabled?: boolean;
  defaultValue?: { value: string; lat: number; lng: number; address: string };
  setValue: any;
  defaultLocation?: { lat: number; lng: number };
  enterManual?: boolean;
}

const DropDownMap: React.FC<DropDownMapProps> = ({
  label,
  name,
  register,
  errors,
  disabled = false,
  defaultValue,
  setValue,
  enterManual = false,
  defaultLocation = { lat: 13.0827, lng: 80.2707 }, // Default to Chennai
}) => {
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState(defaultValue?.address || "");
  const [mapLocation, setMapLocation] = useState(
    defaultValue ? { lat: defaultValue.lat, lng: defaultValue.lng } : defaultLocation
  );
  const [selected, setSelected] = useState<{
    value: string;
    lat: number;
    lng: number;
    address: string;
  } | null>(defaultValue || null);

  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const geocoderRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const searchBoxRef = useRef<any>(null);

  // Use centralized Google Maps context
  const { isLoaded } = useGoogleMaps();

  // Register the input with React Hook Form
  const { ref, ...rest } = register(name);

  // Update state when defaultValue changes
  useEffect(() => {
    if (defaultValue) {
      setAddress(defaultValue.address);
      setMapLocation({ lat: defaultValue.lat, lng: defaultValue.lng });
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  // Google Maps is now loaded centrally via context - no need to load it here

  // Initialize map when modal opens and Google Maps is loaded
  useEffect(() => {
    if (!open || !isLoaded || !mapContainerRef.current) return;

    // Initialize the map
    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: mapLocation,
      zoom: 14,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Create a marker for the selected location
    const marker = new window.google.maps.Marker({
      position: mapLocation,
      map: map,
      draggable: true,
    });

    // Create geocoder for address search
    const geocoder = new window.google.maps.Geocoder();

    // Add event listener for marker drag end
    marker.addListener("dragend", () => {
      const position = marker.getPosition();
      if (position) {
        const lat = position.lat();
        const lng = position.lng();
        setMapLocation({ lat, lng });

        // Update address field with reverse geocoding
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
          if (status === "OK" && results && results[0]) {
            const newAddress = results[0].formatted_address;
            setAddress(newAddress);
          }
        });
      }
    });

    // Add click event listener to the map
    map.addListener("click", (event: any) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      marker.setPosition({ lat, lng });
      setMapLocation({ lat, lng });

      // Update address field with reverse geocoding
      geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
        if (status === "OK" && results && results[0]) {
          const newAddress = results[0].formatted_address;
          setAddress(newAddress);
        }
      });
    });

    // Store references
    mapRef.current = map;
    markerRef.current = marker;
    geocoderRef.current = geocoder;

    // Create search box for the map
    const input = document.createElement("input");
    input.id = "map-search-input";
    input.className = "controls px-5 py-3 mt-2 border rounded-md w-64 shadow-md";
    input.type = "text";
    input.placeholder = "Search for places, addresses, or landmarks";

    map.controls[window.google.maps.ControlPosition.TOP_CENTER].push(input);

    const searchBox = new window.google.maps.places.SearchBox(input);
    searchBoxRef.current = searchBox;

    // Bias searchBox results towards current map viewport
    map.addListener("bounds_changed", () => {
      searchBox.setBounds(map.getBounds() as google.maps.LatLngBounds);
    });

    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces();
      if (places && places.length > 0) {
        const place = places[0];
        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          map.setCenter({ lat, lng });
          marker.setPosition({ lat, lng });
          setMapLocation({ lat, lng });

          if (place.formatted_address) {
            setAddress(place.formatted_address);
          }
        }
      }
    });

    // Cleanup
    return () => {
      if (markerRef.current) {
        window.google.maps.event.clearInstanceListeners(markerRef.current);
      }
      if (mapRef.current) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }
      if (searchBoxRef.current) {
        window.google.maps.event.clearInstanceListeners(searchBoxRef.current);
      }
    };
  }, [open, mapLocation, isLoaded]);

  // Handle address input
  const handleAddressChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAddress(e.target.value);
  };

  // Geocode address to coordinates
  const geocodeAddress = () => {
    if (!geocoderRef.current || !address) return;

    geocoderRef.current.geocode({ address }, (results: any, status: string) => {
      if (status === "OK" && results && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        setMapLocation({ lat, lng });

        if (mapRef.current) {
          mapRef.current.setCenter({ lat, lng });
        }

        if (markerRef.current) {
          markerRef.current.setPosition({ lat, lng });
        }
      } else {
        alert("Couldn't find that address. Please try again.");
      }
    });
  };

  // Handle map location selection - FIXED to only close when values are selected
  const handleConfirmLocation = () => {
    // Check if we have valid location data
    if (!mapLocation.lat || !mapLocation.lng) {
      alert("Please select a location on the map first.");
      return;
    }

    // Check if we have an address (either from user input or from geocoding)
    if (!address.trim()) {
      alert("Please enter an address or click on the map to get location details.");
      return;
    }

    const locationData = {
      value: "custom",
      lat: mapLocation.lat,
      lng: mapLocation.lng,
      address: address.trim()
    };

    setSelected(locationData);

    // Set the form value using setValue
    if (setValue) {
      try {
        setValue(name, locationData);
      } catch (error) {
        console.error("Error setting form value:", error);
      }
    }

    // Only close the modal after successful validation and setting values
    setOpen(false);
  };

  // Handle modal close - add confirmation if user has made changes
  const handleCloseModal = () => {
    const hasChanges = address !== (defaultValue?.address || "") ||
      mapLocation.lat !== (defaultValue?.lat || defaultLocation.lat) ||
      mapLocation.lng !== (defaultValue?.lng || defaultLocation.lng);

    if (hasChanges) {
      const confirmClose = window.confirm("You have unsaved changes. Are you sure you want to close?");
      if (!confirmClose) return;
    }

    // Reset to original values if closing without saving
    setAddress(defaultValue?.address || "");
    setMapLocation(defaultValue ? { lat: defaultValue.lat, lng: defaultValue.lng } : defaultLocation);
    setOpen(false);
  };

  return (
    <div className="w-full">
      <label htmlFor={name} className="input-label">
        {label ? label : <>&nbsp;</>}
      </label>

      <div className="relative">
        <div className="flex">
          <button
            type="button"
            onClick={() => !disabled && setOpen(true)}
            className={`w-full px-4 py-2 border text-left rounded-lg font-medium bg-white cursor-pointer border-gray-300 text-[14px]
              focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
              ${disabled ? "opacity-50 cursor-not-allowed" : ""}
              ${errors[name] ? "border-red-500" : ""}
            `}
          >
            {selected ?
              <span className="flex items-center justify-between w-full">
                <span className="truncate">{selected.address || "Location selected"}</span>
              </span>
              :
              <span className="text-gray-400">{`Select ${label}`} </span>
            }
          </button>
        </div>

        {/* Hidden input to store the value for React Hook Form */}
        <input
          {...rest}
          ref={ref}
          type="hidden"
          id={name}
          name={name}
          value={selected ? JSON.stringify(selected) : ""}
        />

        {enterManual &&
          <span onClick={() => !disabled && setOpen(true)} className="text-[14px] float-right cursor-pointer text-[#444FBD]">Enter Manually</span>
        }

        {errors[name] && <p className="input-error-message">{errors[name]?.message}</p>}
      </div>

      {/* Map Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-auto py-8">
          <div
            className="bg-white rounded-lg shadow-lg max-w-4xl w-full p-6 m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Select {label || "Location"}</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              {/* Map Section */}
              <div className="flex-1 flex flex-col">
                <div className="h-80 md:h-96 bg-gray-100 rounded-lg relative mb-4">
                  <div ref={mapContainerRef} className="absolute inset-0 rounded-lg"></div>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Click on the map or drag the pin to set the exact location
                </p>
              </div>

              {/* Address Form */}
              <div className="w-full md:w-80 flex flex-col">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={address}
                    onChange={handleAddressChange}
                    className="w-full px-4 py-2 border rounded-md h-24 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter full address here"
                  />
                </div>

                <button
                  type="button"
                  onClick={geocodeAddress}
                  className="mb-4 px-4 py-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 transition-colors"
                >
                  Find on Map
                </button>

                <button
                  type="button"
                  onClick={handleConfirmLocation}
                  className="mt-auto py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  Confirm Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropDownMap;